import React, { useState, useRef } from 'react';
import classNames from 'classnames';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Toolbar } from 'primereact/toolbar';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import {PortService} from "../service/PortService";
import {CountryService} from "../service/CountryService";
import {AutoComplete} from "primereact/autocomplete";
import {useMountEffect} from "primereact";
import {isNullOrUndef} from "chart.js/helpers";
import {FilterMatchMode} from "primereact/api";

export const Ports = () => {

    let emptyPort = {
        id: null,
        name: '',
        country: null
    };

    const [ports, setPorts] = useState(null);
    const [portDialog, setPortDialog] = useState(false);
    const [deletePortDialog, setDeletePortDialog] = useState(false);
    const [port, setPort] = useState(emptyPort);
    const [selectedPort, setSelectedPort] = useState(null);
    const [countries, setCountries] = useState(null);
    const [country, setCountry] = useState(null);
    const [filteredCountries, setFilteredCountries] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const toast = useRef(null);
    const dt = useRef(null);
    const [loading, setLoading] = useState(true);
    const portService = new PortService();
    const countryService = new CountryService();
    const filters = {
        'name': { value: null, matchMode: FilterMatchMode.CONTAINS },
        'country.title': { value: null, matchMode: FilterMatchMode.CONTAINS },
    }

    useMountEffect(() => {
        portService.getPorts()
            .then(data => {
                setPorts(data);
                setLoading(false);
            });
        countryService.getCountries()
            .then(data => setCountries(data));
    });

    const openNew = () => {
        setPort(emptyPort);
        setSubmitted(false);
        setPortDialog(true);
    }

    const hideDialog = () => {
        setSubmitted(false);
        setPortDialog(false);
    }

    const hideDeletePortDialog = () => {
        setDeletePortDialog(false);
    }

    const savePort = () => {
        setSubmitted(true);

        if (port.name.trim() && port.country !== null) {
            let _ports = [...ports];
            let _port = {...port};
            portService.savePort(_port)
                .then(data => {
                    if (port.id) {
                        const index = findIndexById(port.id);
                        _ports[index] = data;
                        toast.current.show({
                            severity: 'success',
                            summary: 'Successful',
                            detail: 'Port Updated',
                            life: 3000
                        });
                    } else {
                        _ports.push(data);
                        toast.current.show({
                            severity: 'success',
                            summary: 'Successful',
                            detail: 'Port Created',
                            life: 3000
                        });
                    }
                    setPorts(_ports);
                    setPortDialog(false);
                    setPort(emptyPort);
                    setCountry(null)
                })
                .catch(error => {
                    toast.current.show({severity: 'error', summary: 'Error', detail: error, life: 3000});
                });
        }
    }

    const editPort = (port) => {
        setPort({ ...port });
        setPortDialog(true);
    }

    const confirmDeletePort = (port) => {
        setPort(port);
        setDeletePortDialog(true);
    }

    const deletePort = () => {
        portService.deletePort(port.id)
            .then(data => {
                if(!isNullOrUndef(data) && !data.success) {
                    setDeletePortDialog(false);
                    setPort(emptyPort);
                    toast.current.show({severity: 'error', summary: 'Error', detail: data.errorMessage, life: 3000});
                } else {
                    let _ports = ports.filter(val => val.id !== port.id);
                    setPorts(_ports);
                    setDeletePortDialog(false);
                    setPort(emptyPort);
                    toast.current.show({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Port Deleted',
                        life: 3000
                    });
                }
            })
            .catch(error => {
                toast.current.show({severity: 'error', summary: 'Error', detail: error, life: 3000});
            });
    }

    const findIndexById = (id) => {
        let index = -1;
        for (let i = 0; i < ports.length; i++) {
            if (ports[i].id === id) {
                index = i;
                break;
            }
        }

        return index;
    }

    const onInputChange = (e, name) => {
        const val = (e.target && e.target.value) || '';
        let _port = { ...port };
        _port[`${name}`] = val;

        setPort(_port);
    }

    const leftToolbarTemplate = () => {
        return (
            <React.Fragment>
                <Button label="New" icon="pi pi-plus" className="p-button-success p-mr-2 p-mb-2" onClick={openNew} />
            </React.Fragment>
        )
    }

    const nameBodyTemplate = (rowData) => {
        return (
            <>
                <span className="p-column-title">Port Name</span>
                {rowData.name}
            </>
        );
    }

    const countryBodyTemplate = (rowData) => {
        return (
            <>
                <span className="p-column-title">Country</span>
                {rowData.country ? rowData.country.title : ''}
            </>
        );
    }

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="actions">
                <Button icon="pi pi-pencil" className="p-button-rounded p-button-success p-button-text p-button-sm"
                        title={'Edit'} onClick={() => editPort(rowData)} />
                <Button icon="pi pi-trash" className="p-button-rounded p-button-danger p-button-text p-button-sm"
                        title={'Delete'} onClick={() => confirmDeletePort(rowData)} />
            </div>
        );
    }

    const header = (
        <div className="table-header">
            <h5 className="p-m-0">Manage Ports</h5>
        </div>
    );

    const portDialogFooter = (
        <>
            <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={hideDialog} />
            <Button label="Save" icon="pi pi-check" className="p-button-text" onClick={savePort} />
        </>
    );
    const deletePortDialogFooter = (
        <>
            <Button label="No" icon="pi pi-times" className="p-button-text" onClick={hideDeletePortDialog} />
            <Button label="Yes" icon="pi pi-check" className="p-button-text" onClick={deletePort} />
        </>
    );

    const searchCountry = (event) => {
        setTimeout(() => {
            let _filteredCountries;
            if (!event.query.trim().length) {
                _filteredCountries = [...countries];
            }
            else {
                _filteredCountries = countries.filter((country) => {
                    return country.title.toLowerCase().startsWith(event.query.toLowerCase());
                });
            }

            setFilteredCountries(_filteredCountries);
        }, 250);
    }

    const itemTemplate = (item) => {
        return (
            <div className="country-item">
                <div>{item.title}</div>
            </div>
        );
    }

    const changeCountryValue = (e) => {
        setCountry(e.value)
        let _port = { ...port };
        _port.country = e.value;
        setPort(_port);
    }

    return (
        <div className="p-grid crud-demo">
            <div className="p-col-12">
                <div className="card">
                    <Toast ref={toast} />
                    <Toolbar className="p-mb-4 p-toolbar" left={leftToolbarTemplate}/>

                    <DataTable ref={dt} value={ports} selection={selectedPort} onSelectionChange={(e) => setSelectedPort(e.value)}
                               dataKey="id" paginator rows={10} rowsPerPageOptions={[5, 10, 25]} className="datatable-responsive" loading={loading}
                               paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                               currentPageReportTemplate="Showing {first} to {last} of {totalRecords} ports"
                               emptyMessage="No ports found." header={header} filterDisplay="row" filters={filters}>
                        <Column field="name" header="Port" sortable filter body={nameBodyTemplate}/>
                        <Column field="country.title" header="Country" sortable filter body={countryBodyTemplate}/>
                        <Column body={actionBodyTemplate} bodyStyle={{textAlign: 'right'}}/>
                    </DataTable>

                    <Dialog visible={portDialog} style={{ width: '450px' }} contentStyle={{display: 'inline-table'}} header="Port Details" modal className="p-fluid" footer={portDialogFooter} onHide={hideDialog}>
                        <div className="p-field">
                            <label htmlFor="name">Port name</label>
                            <InputText id="name" value={port.name} onChange={(e) => onInputChange(e, 'name')} required autoFocus className={classNames({ 'p-invalid': submitted && !port.name })} />
                            {submitted && !port.name && <small className="p-invalid">Port name is required.</small>}
                        </div>
                        <div className="p-field">
                            <label htmlFor="country">Country</label>
                            <AutoComplete value={country} suggestions={filteredCountries} completeMethod={searchCountry} itemTemplate={itemTemplate}
                                          ariaLabelledBy={'title'} field="title" dropdown forceSelection onChange={changeCountryValue} />
                            {submitted && !port.country && <small className="p-invalid">Country is required.</small>}
                        </div>
                    </Dialog>

                    <Dialog visible={deletePortDialog} style={{ width: '450px' }} header="Confirm" modal footer={deletePortDialogFooter} onHide={hideDeletePortDialog}>
                        <div className="confirmation-content">
                            <i className="pi pi-exclamation-triangle p-mr-3" style={{ fontSize: '2rem' }} />
                            {port && <span>Are you sure you want to delete <b>{port.name}</b>?</span>}
                        </div>
                    </Dialog>
                </div>
            </div>
        </div>
    );
}
